<template>
  <div
    :style="styleCard"
    class="card-container make-a-copy"
  >
    <div
      :key="itemId"
      class="annotation-text-card"
    >
      <div
        class="annotation-text-card__editor-field"
      >
        <EditorContent
          class="annotation-text-card__editor"
          :editor="editor"
        />
        <div
          v-if="editor"
          :style="characterCountPosition && displayCount"
          class="annotation-text-card__character-count"
        >
          {{ editor.storage.characterCount.characters() }}/{{ limit }}
        </div>
      </div>
      <div
        v-if="isModalOpen"
        class="annotation-text-card__modal"
      >
        <v-text-field
          v-model="link"
          label="Link"
          outlined
          dense
          clear-icon="mdi-close"
          clearable
          @click:clear="clearLink"
        />
        <DsButton
          class="annotation-text-card__modal-button"
          :disabled="hasLink"
          @custom-change="open-modal"
          @click="saveLink"
        >
          {{ $t('components.AnnotationTextCard.button.confirm') }}
        </DsButton>
      </div>
      <div
        v-if="showModal"
        class="annotation-text-card__toolbar-field"
        :style="menuBarPosition"
      >
        <MenuBar
          class="annotation-text-card__toolbar"
          :editor="editor"
          @toggle-modal="toggleModal"
        />
      </div>
    </div>
    <div
      v-if="showOptionsButton"
      class="card-container__options-button"
    >
      <OptionsButton
        :options-data="optionsData"
        :expanded-mode="card.props.isExpandedCard"
        @on-option-menu-click="callOptionMenu"
      />
    </div>
  </div>
</template>

<script>
import { generateRandomId } from '@/utils/math';
import { Editor, EditorContent } from '@tiptap/vue-2';
import MenuBar from '@/components/MenuBar.vue';
import OptionsButton from '@/components/OptionsButton';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Text from '@tiptap/extension-text';
import TextStyle from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import CharacterCount from '@tiptap/extension-character-count';
import Placeholder from '@tiptap/extension-placeholder';
import cardsModel from '@/mixins/cards-model';
import exportModel from '@/mixins/export-model';
import { DsButton } from '@ds/components';
import { trackEventWithUserProperties } from '@/utils/mixpanel';

export default {
  name: 'AnnotationTextCard',
  components: {
    EditorContent,
    MenuBar,
    DsButton,
    OptionsButton,
  },
  mixins: [
    cardsModel,
    exportModel,
  ],
  props: {
    itemId: {
      type: [String, Number],
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      styleCard: {
        background: '',
        transition: '',
      },
      displayCount: {
        display: 'none',
      },
      previousContent: '',
      placeholder: this.$t('components.AnnotationTextCard.placeholder'),
      editor: null,
      limit: 2000,
      editorText: '',
      isModalOpen: false,
      showModal: false,
      link: '',
      hasProtocol: /^((http|https|ftp):\/\/)/u,
    };
  },
  computed: {
    menuBarPosition() {
      const value = this.heightComputedValue;
      const topDistance = `top: ${value + 40}px`;

      return topDistance;
    },
    characterCountPosition() {
      const value = this.heightComputedValue;
      return `top: ${value + 8}px`;
    },
    heightComputedValue() {
      const heightWithoutPadding = this.$attrs.height * 50;
      const heightPadding = (this.$attrs.height - 1) * 16;
      return heightWithoutPadding + heightPadding - 50;
    },
    heightComputed() {
      const value = this.heightComputedValue;
      return `height: ${value}px`;
    },
    hasLink() {
      return Boolean(!this.link);
    },
    optionsData() {
      return this.baseCardOptions.filter(({ name }) => name !== 'Filtrar')
        .concat(this.exportOptionsData)
        .sort((actualOption, prevOption) => this.sortMenuOptions(actualOption, prevOption));
    },
    showOptionsButton() {
      return Boolean(this.optionsData.length);
    },
  },
  mounted() {
    this.editor = new Editor({
      injectCSS: false,
      extensions: [
        StarterKit,
        Underline,
        TextStyle,
        Text,
        Link.configure({
          validate: href => /^https?:\/\//u.test(href),
        }),
        Highlight.configure({
          HTMLAttributes: {
            class: 'highlighted',
          },
        }),
        CharacterCount.configure({
          limit: this.limit,
        }),
        Placeholder.configure({
          placeholder: this.placeholder,
        }),
      ],
      onCreate: () => {
        this.editor.commands.insertContent(this.card.props.text);
      },
      onUpdate: () => {
        this.editorText = this.editor.getHTML();
      },
      onFocus: () => {
        this.previousContent = this.card.props.text;
        this.isModalOpen = false;
        this.showModal = true;
        this.changeBackground();
      },
      onBlur: ({ event }) => {
        if (event.relatedTarget) {
          return;
        }

        if (this.previousContent === this.editorText || this.editorText === null) {
          trackEventWithUserProperties('ST TextCard CancelButton Edit');
        } else {
          trackEventWithUserProperties('ST TextCard SaveButton Edit');
        }

        this.saveText();
      },
    });
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  methods: {
    saveText() {
      this.card.props.text = this.editorText;
      this.card.id = generateRandomId();
      this.saveCards();
    },
    changeBackground() {
      if (this.editor.isFocused) {
        this.styleCard.background = 'var(--ds-color-overlay) !important';
        this.styleCard.transition = '0.2s ease-in-out';
        this.displayCount.display = 'block';
      }
    },
    setLink(value) {
      value ? this.editor.commands.toggleLink({ href: value, target: '_blank' }) : this.editor.commands.unsetLink();
    },
    saveLink() {
      if (!this.hasProtocol.test(this.link)) {
        this.link = `http://${this.link}`;
      }

      this.setLink(this.link);
      this.isModalOpen = false;
    },
    clearLink() {
      this.link = '';
      this.setLink(this.link);
      this.isModalOpen = false;
    },
    toggleModal() {
      this.link = this.editor.getAttributes('link').href;
      this.isModalOpen = true;
    },
  },
};
</script>

<style scoped lang="scss">
.card-container {
  display: flex;
  padding: var(--ds-spacing-stack-sm) var(--ds-spacing-stack-xs) var(--ds-spacing-stack-xs) var(--ds-spacing-stack-xs);
  width: 100%;
  height: 100%;
}

.annotation-text-card {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  box-shadow: none !important;
  border-radius: var(--ds-border-radius-md);

  &__editor-field {
    display: flex;
    align-self: start;
    flex-direction: column;
    align-items: flex-end;
    width: 100%;
    height: 100%;
    -webkit-scrollbar: 8px;
  }

  &__editor {
    padding: var(--ds-spacing-stack-nano);
    width: 100%;
    height: calc(100% - 18px);
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: var(--ds-spacing-stack-nano);
    }
  }

  &__character-count {
    padding-right: var(--ds-spacing-stack-nano);
    font-style: normal;
    font-weight: var(--ds-font-weight-regular);
    font-size: var(--ds-font-size-xs);
    line-height: var(--ds-font-line-height-dense);
    color: var(--ds-color-text-2);
  }

  &__toolbar-field {
    width: 100%;
    height: 40px;
    position: absolute;
    display: flex;
    justify-content: center;
  }

  &__toolbar {
    background: var(--ds-color-surface-contrast) !important;
  }

  &__modal-button {
    margin-top: var(--ds-spacing-stack-nano);
  }

  &__modal {
    position: absolute;
    width: 300px;
    background: var(--ds-color-surface-contrast);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;
    box-shadow: 0 2px 24px -6px rgba(0, 0, 0, 0.32), 0 2px 64px -4px rgba(0, 0, 0, 0.32);
    padding: var(--ds-spacing-stack-xs);
    border-radius: var(--ds-border-radius-sm);
  }
}

::v-deep {
  .v-input {
    flex: 0;
  }

  .v-input__control {
    display: flex;
    align-items: center;
  }

  .v-input__slot {
    width: 268px;
    height: 32px;

    input {
      text-overflow: ellipsis;
    }
  }

  .v-text-field__details {
    display: none;
  }

  .ProseMirror {
    height: 80%;

    h1 {
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-md);
      line-height: var(--ds-font-line-height-normal);
      color: var(--ds-color-text-1);
    }

    h2 {
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-baseline);
      line-height: var(--ds-font-line-height-normal);
      color: var(--ds-color-text-1);
    }

    p {
      font-weight: var(--ds-font-weight-regular);
      font-size: var(--ds-font-size-sm);
      line-height: var(--ds-font-line-height-loose);
      margin: 0;
      color: var(--ds-color-text-1);
    }

    ol {
      margin: var(--ds-spacing-inline-nano) 0;
      padding-left: 18px;

      li::marker {
        font-weight: var(--ds-font-weight-regular);
        font-size: var(--ds-font-size-sm);
        line-height: var(--ds-font-line-height-loose);
        color: var(--ds-color-text-1);
      }
    }

    ul {
      margin: var(--ds-spacing-inline-nano) 0;
      padding-left: 18px;

      li::marker {
        font-weight: var(--ds-font-weight-regular);
        font-size: var(--ds-font-size-sm);
        line-height: var(--ds-font-line-height-loose);
        color: var(--ds-color-text-1);
      }
    }

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: var(--ds-color-text-2);
      font-weight: var(--ds-font-weight-regular);
      font-size: var(--ds-font-size-sm);
      line-height: var(--ds-font-line-height-loose);
      pointer-events: none;
      height: 0;
    }

    .highlighted {
      background-color: var(--ds-color-border-color);
      color: var(--ds-color-text-1);
      padding: 0 2px;
    }
  }

  .ProseMirror-focused {
    outline: none;
  }
}
</style>
